/**
 * Die mitlaufenden Summen der Maske „Neue Rechnung" (CRM-152).
 *
 * EINE VORSCHAU, KEINE RECHNUNG. Verbindlich rechnet der Entwurf auf dem
 * Server; die Maske zeigt, was er voraussichtlich ergibt. Damit die Vorschau
 * nicht um Cent danebenliegt, folgt sie denselben Regeln: Festkomma statt
 * Fließkomma (RND-01), gerundet je Position (RND-05), die Steuer je
 * Steuersatzgruppe (RND-07), bei Bruttopreisführung der Bruttobetrag als
 * führender Wert (RND-08).
 */

export type Vorschauzeile = {
  readonly menge: number;
  /** Einzelpreis in der Preisführung des Belegs, Dezimalpunkt; leer heißt: noch kein Preis. */
  readonly einzelpreis: string;
  /** Steuersatz in Prozent, etwa „19.00"; leer heißt: noch keine Steuer. */
  readonly steuerProzent: string;
};

export type Vorschausummen = {
  readonly netto: string;
  readonly steuer: string;
  readonly brutto: string;
  /** Ob jede Zeile Preis und Steuer trägt — sonst ist die Summe unvollständig. */
  readonly vollstaendig: boolean;
};

/** Vier Nachkommastellen als ganze Zahl — die Preise tragen höchstens vier. */
const SKALA = 10_000n;

function festkomma(wert: string): bigint | undefined {
  const t = wert.trim().replace(',', '.');
  const treffer = /^(\d{1,10})(?:\.(\d{1,4}))?$/.exec(t);
  if (treffer === null) return undefined;
  return BigInt(treffer[1]!) * SKALA + BigInt((treffer[2] ?? '').padEnd(4, '0'));
}

/** RND-02: kaufmännisch, ab der halben Einheit vom Nullpunkt weg. */
function teileGerundet(zaehler: bigint, nenner: bigint): bigint {
  const halb = nenner / 2n;
  return zaehler >= 0n ? (zaehler + halb) / nenner : -((-zaehler + halb) / nenner);
}

/** Cent als Betrag mit zwei Stellen. */
function betrag(cent: bigint): string {
  const negativ = cent < 0n;
  const b = negativ ? -cent : cent;
  return `${negativ ? '-' : ''}${(b / 100n).toString()}.${(b % 100n).toString().padStart(2, '0')}`;
}

/** Ein Einzelpreis als Betrag mit zwei Stellen für die Anzeige; leer, wenn er nicht lesbar ist. */
export function zeilenbetrag(zeile: Vorschauzeile): string {
  const preis = festkomma(zeile.einzelpreis);
  if (preis === undefined || !Number.isInteger(zeile.menge) || zeile.menge < 1) return '';
  // RND-05: je Position auf den Cent.
  return betrag(teileGerundet(preis * BigInt(zeile.menge), SKALA / 100n));
}

export function vorschausummen(
  zeilen: readonly Vorschauzeile[],
  preisfuehrung: 'netto' | 'brutto',
): Vorschausummen {
  /** Zeilenbetrag in Cent je Steuersatz (in Hundertstel Prozent). */
  const gruppen = new Map<bigint, bigint>();
  let vollstaendig = zeilen.length > 0;
  for (const z of zeilen) {
    const preis = festkomma(z.einzelpreis);
    const satz = festkomma(z.steuerProzent);
    if (preis === undefined || satz === undefined || !Number.isInteger(z.menge) || z.menge < 1) {
      vollstaendig = false;
      continue;
    }
    // RND-05: je Position gerundet, Summen nur aus gerundeten Werten.
    const cent = teileGerundet(preis * BigInt(z.menge), SKALA / 100n);
    const schluessel = satz / 100n;
    gruppen.set(schluessel, (gruppen.get(schluessel) ?? 0n) + cent);
  }

  let netto = 0n;
  let steuer = 0n;
  for (const [satzHundertstel, summe] of gruppen) {
    if (preisfuehrung === 'netto') {
      // RND-07: Gruppennetto mal Satz, auf den Cent.
      const s = teileGerundet(summe * satzHundertstel, 10_000n);
      netto += summe;
      steuer += s;
    } else {
      // RND-08: Netto = Brutto / (1 + Satz) auf zwei Stellen, die Steuer ist die Differenz.
      const n = teileGerundet(summe * 10_000n, 10_000n + satzHundertstel);
      netto += n;
      steuer += summe - n;
    }
  }
  return { netto: betrag(netto), steuer: betrag(steuer), brutto: betrag(netto + steuer), vollstaendig };
}

/**
 * Ein gepflegter Preis in der Preisführung des Belegs (RND-08): Der Katalog
 * kennt netto und brutto gepflegte Preise; die Maske zeigt den Betrag, den
 * der Beleg führt.
 */
export function inPreisfuehrung(
  einzelpreis: string,
  istBrutto: boolean,
  steuerProzent: string,
  preisfuehrung: 'netto' | 'brutto',
): string {
  const preis = festkomma(einzelpreis);
  const satz = festkomma(steuerProzent);
  if (preis === undefined || satz === undefined) return einzelpreis;
  const hundertstel = satz / 100n;
  const ziel =
    istBrutto === (preisfuehrung === 'brutto')
      ? teileGerundet(preis, 100n)
      : istBrutto
        ? teileGerundet(preis * 10_000n, (10_000n + hundertstel) * 100n)
        : teileGerundet(preis * (10_000n + hundertstel), 10_000n * 100n);
  return betrag(ziel);
}

/**
 * CRM-174: der Bruttobetrag der Vorschau abzüglich der erfassten Anrechnungen,
 * nicht unter null — nur, um einem Wertgutschein beim Prüfen den offenen
 * Betrag zu nennen. Was er wirklich anrechnet, legt der Server beim Anlegen
 * fest (GUT-14: höchstens der offene Betrag des Entwurfs).
 */
export function restbetrag(brutto: string, abzuege: readonly string[]): string {
  let rest = festkomma(brutto) ?? 0n;
  for (const a of abzuege) rest -= festkomma(a) ?? 0n;
  return betrag(teileGerundet(rest > 0n ? rest : 0n, 100n));
}

/** CRM-174: Beträge zusammengezählt, etwa die Guthaben eines Kunden für den Hinweis. */
export function summeDerBetraege(betraege: readonly string[]): string {
  let summe = 0n;
  for (const b of betraege) summe += festkomma(b) ?? 0n;
  return betrag(teileGerundet(summe, 100n));
}
